import { Launch } from '@mui/icons-material';
import { Box, IconButton, SxProps, Theme, Tooltip, useMediaQuery, useTheme } from '@mui/material';
import React, { useCallback } from 'react';
import { useModuleDrawerContext } from '../../../../contexts/ModuleDrawerContext';
import { UserConfigurableModule } from '../../../../types/User';
import AppLink from '../../AppLink/AppLink';
import { drawerModulePaths } from './ModuleDrawer';

export const triggerBaseStyles: SxProps<Theme> = {
  cursor: 'pointer',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  maxWidth: '100%',
  width: '100%',
  display: 'block',
  minWidth: 0,
  textDecoration: 'underline',
};

export const drawerLinkContainerStyles: SxProps<Theme> = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 0.75,
  minWidth: 0,
  '&:hover .drawer-link-launch-icon': {
    opacity: 1,
  },
};

export const launchIconWrapperStyles: SxProps<Theme> = {
  opacity: 0,
  transition: 'opacity 0.15s ease-in-out',
  flexShrink: 0,
};

type DrawerLinkProps = React.PropsWithChildren<{
  module: UserConfigurableModule;
  uuid: string;
}>;

const DrawerLink: React.FC<DrawerLinkProps> = ({ module, uuid, children }) => {
  if (!children || !uuid || !module) return null;

  const { setModuleDrawerState } = useModuleDrawerContext();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const url = `/${drawerModulePaths[module]}/${uuid}`;

  const openDrawer = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      setModuleDrawerState({ module, uuid });
    },
    [module, uuid, setModuleDrawerState]
  );

  return (
    <Box
      sx={{
        ...drawerLinkContainerStyles,
        '&:hover .drawer-link-launch-icon': {
          opacity: isMobile ? 0 : 1,
        },
      }}
    >
      <Box onClick={openDrawer} sx={{ ...triggerBaseStyles }}>
        {children}
      </Box>

      {!isMobile && (
        <Tooltip title="Open full page">
          <Box component="span" sx={{ display: 'inline-flex', lineHeight: 0 }}>
            <IconButton
              component={AppLink}
              to={url}
              size="small"
              onClick={(e) => e.stopPropagation()}
              className="drawer-link-launch-icon"
              sx={launchIconWrapperStyles}
            >
              <Launch fontSize="inherit" />
            </IconButton>
          </Box>
        </Tooltip>
      )}
    </Box>
  );
};

export default DrawerLink;
